import Button from 'components/Button'
import Modal from 'components/Modal'
import ToastContext from 'containers/ToastManager/ToastContext'
import {
  ProjectFragment,
  ShareListItemFragment,
  useRemoveShareFromProjectMutation,
} from 'gqlTypes'
import {
  readProjectList,
  updateShareWithProjectFragment,
} from 'modules/projects/operations'
import React, { useContext } from 'react'
import styled from 'styled-components'
import { removeFromPaginated } from 'utils/apolloCache'
import { ModalInjectedProps } from 'containers/ModalManager'

import { StyledDraftsWarningContainer } from './RemoveShareFromProject.styles'
import { ErrorHandler } from 'modules/tracing'
import { routes } from 'constants/routes'
import { Link } from 'react-router-dom'

interface RemoveShareFromProjectProps extends ModalInjectedProps {
  project: ProjectFragment
  share: Pick<ShareListItemFragment, 'identifier' | 'name' | 'workspace'>
}

interface ShareIdentity {
  identifier: string
  readonly __typename: 'Share'
}

const WordBreaker = styled.span`
  overflow-wrap: break-word;
`

const RemoveShareFromProject: React.FC<RemoveShareFromProjectProps> = props => {
  const { hideModal, project, share } = props
  const { showToast } = useContext(ToastContext)

  const [removeFromProject, { loading }] = useRemoveShareFromProjectMutation({
    redirectErrors: true,
    variables: { shareId: share.identifier, projectId: project.identifier },
    update: (cache, data) => {
      if (!data) {
        ErrorHandler.shouldNeverHappen.invalidMutationData(
          'removeShareFromProject'
        )
        return
      }

      const cacheResult = readProjectList(cache, {
        projectId: project.shortId,
      })

      if (cacheResult) {
        removeFromPaginated<ShareIdentity, typeof cacheResult.project>(
          cache,
          { identifier: share.identifier, __typename: 'Share' },
          cacheResult.project
        )
      }

      // Update the Share cache
      updateShareWithProjectFragment(cache, share.identifier, share => {
        share.project = null
      })
    },
    onCompleted: () => {
      showToast(
        <>
          Document removed from &quot;
          <Link
            to={routes.WORKSPACE_PROJECT.create({
              projectId: project.shortId,
              workspaceId: share.workspace.identifier,
            })}>
            {project.name}
          </Link>
          &quot;
        </>
      )
      hideModal()
    },
    onError: 'show-toast',
  })

  const isDraft = project.type === 'PERSONAL_DRAFTS'

  return (
    <Modal
      title={`Remove Document from ${isDraft ? 'My Drafts' : 'Project'}?`}
      onCancel={hideModal}>
      <Modal.Body>
        {isDraft ? (
          <StyledDraftsWarningContainer>
            <p>
              <WordBreaker>
                “<strong>{share.name}</strong>”
              </WordBreaker>{' '}
              will be removed from My Drafts. You can find it in the All
              Documents view and everyone in the Workspace can access it.
            </p>
            <br />
            <Modal.Explainer>
              Documents cannot be moved back to My Drafts.
            </Modal.Explainer>
          </StyledDraftsWarningContainer>
        ) : (
          <p>
            <WordBreaker>
              “<strong>{share.name}</strong>”
            </WordBreaker>{' '}
            will be removed from the{' '}
            <WordBreaker>
              “<strong>{project.name}</strong>”
            </WordBreaker>{' '}
            Project. You can find it in the All Documents view.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={hideModal} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          loading={loading}
          onClick={() => removeFromProject()}>
          Remove Document
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default RemoveShareFromProject
